import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import RestoreListingPrompt from "./components/RestoreListingPrompt";
import RealNamePrompt from "./components/RealNamePrompt";
import ProfileCompletionPrompt from "./components/ProfileCompletionPrompt";
import AdminRoute from "./components/AdminRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import GlobalImageLightbox from "./components/GlobalImageLightbox";
import { Navigation } from "./components/landing/Navigation";
import { LandingPage } from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import OnboardingPage from "./pages/OnboardingPage";
import MyPostsPage from "./pages/MyPostsPage";
import MyCornerPage from "./pages/MyCornerPage";
import { CommunityPage } from "./pages/CommunityPage";
import CommunityFeedPage from "./pages/CommunityFeedPage";
import EmotionalTimelinePage from "./pages/EmotionalTimelinePage";
import FollowListPage from "./pages/FollowListPage";
import AdmirersPage from "./pages/AdmirersPage";
import WorryRoomPage from "./pages/WorryRoomPage";
import CommentNotificationsPage from "./pages/CommentNotificationsPage";
import { WritePostPage } from "./pages/WritePostPage";
import { PostDetailPage } from "./pages/PostDetailPage";
import { ProfilePage } from "./pages/ProfilePage";
import { MessagesPage } from "./pages/MessagesPage";
import { MembershipPage } from "./pages/MembershipPage";
import MembershipSuccessPage from "./pages/MembershipSuccessPage";
import { CurriculumDetailPage } from "./pages/CurriculumDetailPage";
import { AdminCurriculumPage } from "./pages/AdminCurriculumPage";
import { DatingAgencyPage } from "./pages/DatingAgencyPage";
import { OfflineEventsPage } from "./pages/OfflineEventsPage";
import { EventPaymentPage } from "./pages/EventPaymentPage";
import { ChatPage } from "./pages/ChatPage";
import { GroupChatPage } from "./pages/GroupChatPage";
import GroupChatLandingPage from "./pages/GroupChatLandingPage";
import CounselingPage from "./pages/CounselingPage";
import LoveProjectPage from "./pages/LoveProjectPage";
import GroupMeetingPage from "./pages/GroupMeetingPage";
import ZoomDatePage from "./pages/ZoomDatePage";
import GlobalZoomMeetingPage from "./pages/GlobalZoomMeetingPage";
import LoveAssessmentPage from "./pages/LoveAssessmentPage";
import NewsPage from "./pages/NewsPage";
import AdminNewsPage from "./pages/AdminNewsPage";
import AdminCommunityNewsPage from "./pages/AdminCommunityNewsPage";
import DatingAppPage from "./pages/DatingAppPage";
import ImageConsultingPage from "./pages/ImageConsultingPage";
import AdminImageConsultingPage from "./pages/AdminImageConsultingPage";
import MannerClassPage from "./pages/MannerClassPage";
import AdminMannerClassPage from "./pages/AdminMannerClassPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminEventsPage from "./pages/AdminEventsPage";
import AdminDatingAgencyPage from "./pages/AdminDatingAgencyPage";
import DatingAgencyPaymentSuccessPage from "./pages/DatingAgencyPaymentSuccessPage";
import AdminPostsPage from "./pages/AdminPostsPage";
import AdminMessagesPage from "./pages/AdminMessagesPage";
import AdminLegacyImportPage from "./pages/AdminLegacyImportPage";
import AdminLegacyActivatePage from "./pages/AdminLegacyActivatePage";
import LegacyActivatePage from "./pages/LegacyActivatePage";
import AdminBulkOpsPage from "./pages/AdminBulkOpsPage";
import AdminBulkEmailPage from "./pages/AdminBulkEmailPage";
import EventsPage from "./pages/EventsPage";
import PricingPage from "./pages/PricingPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import SelfIntroPage from "./pages/SelfIntroPage";
import WelcomeIntroPage from "./pages/WelcomeIntroPage";
import DocumentUploadPage from "./pages/DocumentUploadPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import MembersPage from "./pages/MembersPage";
import CoupleBoardPage from "./pages/CoupleBoardPage";
import CoupleBoardDetailPage from "./pages/CoupleBoardDetailPage";
import CoupleBoardWritePage from "./pages/CoupleBoardWritePage";
import CoupleChatPage from "./pages/CoupleChatPage";
import PointsPage from "./pages/PointsPage";
import CreditsPage from "./pages/CreditsPage";
import AboutPage from "./pages/AboutPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import CommunityGuidelinesPage from "./pages/CommunityGuidelinesPage";
import LikedPostsPage from "./pages/LikedPostsPage";
import SmartMatchPage from "./pages/SmartMatchPage";
import AuthorPostsPage from "./pages/AuthorPostsPage";
import AdminCounselingPage from "./pages/AdminCounselingPage";
import AdminMigrationPage from "./pages/AdminMigrationPage";
import LoveAcademyPage from "./pages/LoveAcademyPage";
import AdminDocumentVerificationPage from "./pages/AdminDocumentVerificationPage";
import AdminPreRegisterPage from "./pages/AdminPreRegisterPage";
import AdminPaymentsPage from "./pages/AdminPaymentsPage";
import AdminLizCouplesPage from "./pages/AdminLizCouplesPage";
import AdminMemberSearchPage from "./pages/AdminMemberSearchPage";
import AdminAdsPage from "./pages/AdminAdsPage";
import AdminPayPalPage from "./pages/AdminPayPalPage";
import FAQPage from "./pages/FAQPage";
import SupportPage from "./pages/SupportPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import LizCouplesPage from "./pages/LizCouplesPage";
import CoupleStoriesPage from "./pages/CoupleStoriesPage";
import ReceivedInterestsPage from "./pages/ReceivedInterestsPage";
import PrivateChatPage from "./pages/PrivateChatPage";
import PrivateCounselingPage from "./pages/PrivateCounselingPage";
import LizCounselingPage from "./pages/LizCounselingPage";

// 페이지 이동 시 스크롤을 맨 위로 (hash가 있으면 해당 섹션으로)
function ScrollToTop() {
  const { pathname, hash, search } = useLocation();
  
  useEffect(() => {
    // Save referral code from URL
    const params = new URLSearchParams(search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("nc_ref_code", ref);
  }, [search]);
  
  useEffect(() => {
    if (hash) {
      // hash가 있으면 해당 섹션으로 스크롤
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      // hash가 없으면 맨 위로
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  
  return null;
}

// Google Analytics 4 - SPA route page_view tracker
function GAPageTracker() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  return null;
}

function HomeRedirect() {
  // liztv.ca 에서 넘어온 사용자는 항상 랜딩 페이지를 보도록 강제
  const fromLiztv = (() => {
    if (typeof window === "undefined") return false;
    const url = new URL(window.location.href);
    if (url.searchParams.get("from") === "liztv") return true;
    const ref = document.referrer || "";
    if (ref.includes("liztv.ca")) return true;
    // 세션 내에서 한 번 감지되면 유지 (페이지 이동해도 랜딩 우선)
    if (sessionStorage.getItem("nc_from_liztv") === "true") return true;
    return false;
  })();

  const shouldRedirect = useRef(
    !fromLiztv && localStorage.getItem("nc_visited") === "true"
  );

  useEffect(() => {
    if (fromLiztv) {
      sessionStorage.setItem("nc_from_liztv", "true");
    }
    if (!shouldRedirect.current) {
      localStorage.setItem("nc_visited", "true");
    }
  }, [fromLiztv]);

  if (shouldRedirect.current) {
    return <Navigate to="/community" replace />;
  }
  return <LandingPage />;
}

function App() {
  return (
    <AuthProvider>
      <div className="font-sans">
        <Toaster richColors position="top-center" />
        <BrowserRouter>
          <ScrollToTop />
          <GAPageTracker />
          {/* 전역 이미지 라이트박스 — 어느 페이지에서든 사진 클릭 시 확대 */}
          <GlobalImageLightbox />
          {/* Global Navigation - visible on all pages */}
          <Navigation />
          {/* 숨김 처리된 회원이 로그인 시 다시 노출 여부 묻기 */}
          <RestoreListingPrompt />
          {/* 정회원 이상인데 실명 미입력 시 프로필 보완 안내 팝업 */}
          <RealNamePrompt />
          {/* 정회원(특히 레거시) 추가정보(성별/나이/지역/사진/소개) 입력 유도 */}
          <ProfileCompletionPrompt />
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            {/* /login 은 별도 페이지가 아니라 홈에서 모달로 처리 — 이전 링크 호환을 위해 홈으로 리다이렉트 */}
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/signup" element={<Navigate to="/" replace />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/welcome-intro" element={<WelcomeIntroPage />} />
            <Route path="/my-posts" element={<MyPostsPage />} />
            <Route path="/admirers" element={<AdmirersPage />} />
            <Route path="/worries" element={<WorryRoomPage />} />
            <Route path="/live-talk" element={<Navigate to="/worries" replace />} />
            <Route path="/comment-notifications" element={<CommentNotificationsPage />} />
            <Route path="/my-corner" element={<MyCornerPage />} />
            <Route path="/community" element={<CommunityFeedPage />} />
            <Route path="/community/board" element={<CommunityPage />} />
            <Route path="/community/write" element={<WritePostPage />} />
            <Route path="/community/post/:postId" element={<PostDetailPage />} />
            <Route path="/community/author/:authorId" element={<AuthorPostsPage />} />
            <Route path="/timeline" element={<EmotionalTimelinePage />} />
            <Route path="/timeline/:userId" element={<EmotionalTimelinePage />} />
            <Route path="/timeline/:userId/followers" element={<FollowListPage kind="followers" />} />
            <Route path="/timeline/:userId/following" element={<FollowListPage kind="following" />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/received-interests" element={<ReceivedInterestsPage />} />
            <Route path="/chat/:roomId" element={<PrivateChatPage />} />
            {/* Stripe 기반 멤버십 페이지는 PayPal로 통합 — /pricing 으로 리다이렉트 */}
            <Route path="/membership" element={<Navigate to="/pricing" replace />} />
            <Route path="/membership/success" element={<MembershipSuccessPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/profile/intro" element={<SelfIntroPage />} />
            <Route path="/profile/document" element={<DocumentUploadPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="/curriculum/:curriculumId" element={<CurriculumDetailPage />} />
            <Route path="/admin/curriculum" element={<AdminRoute><AdminCurriculumPage /></AdminRoute>} />
            <Route path="/dating-agency" element={<DatingAgencyPage />} />
            <Route path="/dating-agency/payment-success" element={<DatingAgencyPaymentSuccessPage />} />
            <Route path="/offline-events" element={<OfflineEventsPage />} />
            <Route path="/event-payment/:eventId" element={<EventPaymentPage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/chat/group" element={<GroupChatLandingPage />} />
            <Route path="/chat/group/:ageGroup" element={<GroupChatPage />} />
            <Route path="/counseling" element={<CounselingPage />} />
            <Route path="/love-project" element={<LoveProjectPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/admin/news" element={<AdminNewsPage />} />
            <Route path="/admin/community-news" element={<AdminCommunityNewsPage />} />
            <Route path="/love-academy" element={<LoveAcademyPage />} />
            <Route path="/group-meeting" element={<GroupMeetingPage />} />
            <Route path="/zoom-date" element={<ZoomDatePage />} />
            <Route path="/global-zoom" element={<GlobalZoomMeetingPage />} />
            <Route path="/love-test" element={<LoveAssessmentPage />} />
            <Route path="/dating-app" element={<DatingAppPage />} />
            <Route path="/image-consulting" element={<ImageConsultingPage />} />
            <Route path="/admin/image-consulting" element={<AdminRoute><AdminImageConsultingPage /></AdminRoute>} />
            <Route path="/manner-class" element={<MannerClassPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/members" element={<MembersPage />} />
            <Route path="/couple-board" element={<CoupleBoardPage />} />
            <Route path="/couple-board/write" element={<CoupleBoardWritePage />} />
            <Route path="/couple-board/:postId" element={<CoupleBoardDetailPage />} />
            <Route path="/couple-chat" element={<CoupleChatPage />} />
            <Route path="/points" element={<PointsPage />} />
            <Route path="/credits" element={<CreditsPage />} />
            <Route path="/smart-match" element={<SmartMatchPage />} />
            <Route element={<AdminRoute />}>
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/manner-class" element={<AdminMannerClassPage />} />
              <Route path="/admin/events" element={<AdminEventsPage />} />
              <Route path="/admin/dating-agency" element={<AdminDatingAgencyPage />} />
              <Route path="/admin/posts" element={<AdminPostsPage />} />
              <Route path="/admin/messages" element={<AdminMessagesPage />} />
              <Route path="/admin/legacy-import" element={<AdminLegacyImportPage />} />
              <Route path="/admin/legacy-activate" element={<AdminLegacyActivatePage />} />
              <Route path="/admin/bulk-ops" element={<AdminBulkOpsPage />} />
              <Route path="/admin/bulk-email" element={<AdminBulkEmailPage />} />
              <Route path="/admin/counseling" element={<AdminCounselingPage />} />
              <Route path="/admin/migration" element={<AdminMigrationPage />} />
              <Route path="/admin/documents" element={<AdminDocumentVerificationPage />} />
              <Route path="/admin/pre-register" element={<AdminPreRegisterPage />} />
              <Route path="/admin/payments" element={<AdminPaymentsPage />} />
              <Route path="/admin/liz-couples" element={<AdminLizCouplesPage />} />
              <Route path="/admin/member-search" element={<AdminMemberSearchPage />} />
              <Route path="/admin/ads" element={<AdminAdsPage />} />
              <Route path="/admin/paypal" element={<AdminPayPalPage />} />
            </Route>
            <Route path="/legacy-activate" element={<LegacyActivatePage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/community-guidelines" element={<CommunityGuidelinesPage />} />
            <Route path="/my-likes" element={<LikedPostsPage />} />
            <Route path="/liz-couples" element={<LizCouplesPage />} />
            <Route path="/couples" element={<CoupleStoriesPage />} />
            <Route path="/couples/:id" element={<CoupleStoriesPage />} />
            <Route path="/private-counseling" element={<PrivateCounselingPage />} />
            <Route path="/liz-counseling" element={<LizCounselingPage />} />
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
